/* eslint-disable max-lines */
import { JsonRpcSigner } from '@ethersproject/providers';
import { ethers } from 'ethers';

import { Erc20Controller } from '@bitcoin-portal/neko-web-sdk';

import {
  APPROVE_MAX_VALUE,
  DEFAULT_DECIMALS,
  DEFAULT_ERROR_MESSAGE,
  TX_STATUS,
  TxType,
  USER_REJECTED_TEXTS,
} from '@context/constants';

import { newrelicError } from '@helpers/index';
import { getStakingContract } from '@helpers/web3';

export const addFarmDeposit = async (
  amountTokenApproved: any,
  decimals: number,
  farmContract: string,
  input: number,
  lpTokenBalance: any,
  pairId: string,
  readableLPTokenBalance: number,
  setTxStatus: (status: TxType) => void,
  setTransactionHash: (hash: string) => void,
  setError: (error: boolean) => void,
  setErrorMessage: (errorMessage: string) => void,
  setView: (view: FarmsView) => void,
  account: string,
  tokenContract?: Erc20Controller,
  signerInstance?: JsonRpcSigner,
  dynamicRewards?: boolean,
): Promise<void> => {
  try {
    const precision = 10 ** decimals;
    const depositingMax =
      input === Math.floor(readableLPTokenBalance * precision) / precision;

    const amountTokenDesired = depositingMax
      ? lpTokenBalance
      : ethers.utils.parseUnits(
          input.toFixed(DEFAULT_DECIMALS),
          DEFAULT_DECIMALS,
        );

    const needApproval = amountTokenDesired.gt(amountTokenApproved);

    if (needApproval) {
      if (tokenContract) {
        const res = await tokenContract.approveToken(
          farmContract,
          APPROVE_MAX_VALUE,
        );

        if (res.isFailure) throw new Error(res.error);

        const approveTransaction = res.getValue();

        await approveTransaction.wait();
      }
    }

    setTxStatus(TX_STATUS.Confirm);

    const stakingContract = getStakingContract(
      farmContract,
      dynamicRewards,
      signerInstance,
    );

    const transaction = await stakingContract.farmDeposit(amountTokenDesired);

    setTxStatus(TX_STATUS.Pending);
    setTransactionHash(transaction.hash);

    await transaction.wait(1);

    setTxStatus(TX_STATUS.Success);
    setView('success');
  } catch (e) {
    newrelicError(e, {
      method: 'addFarmDeposit',
      input,
      pairId,
    });

    const userRejected = USER_REJECTED_TEXTS.some(text =>
      (e as IError).message.includes(text),
    );
    console.log('Error depositing into farm: ', e);

    setError(true);

    if (!userRejected) {
      setErrorMessage(DEFAULT_ERROR_MESSAGE);
    }
    setView('error');
  }
};

export const withdrawFarm = async (
  decimals: number,
  earnedAmount: number,
  farmContract: string,
  input: number,
  stakedBalance: number,
  setTxStatus: (status: TxType) => void,
  setExitedFarm: (approved: boolean) => void,
  setTransactionHash: (hash: string) => void,
  setError: (error: boolean) => void,
  setErrorMessage: (errorMessage: string) => void,
  setView: (view: FarmsView) => void,
  account: string,
  signerInstance?: JsonRpcSigner,
  dynamicRewards?: boolean,
): Promise<void> => {
  try {
    const precision = 10 ** decimals;
    const amountTokenDesired = ethers.utils.parseUnits(
      input.toFixed(DEFAULT_DECIMALS),
      DEFAULT_DECIMALS,
    );

    const withdrawingMax =
      input === Math.floor(stakedBalance * precision) / precision;
    const withdrawOpts = {};

    setExitedFarm(withdrawingMax);
    setTxStatus(TX_STATUS.Confirm);

    const stakingContract = getStakingContract(
      farmContract,
      dynamicRewards,
      signerInstance,
    );

    const transaction =
      withdrawingMax && earnedAmount
        ? await stakingContract.exitFarm(withdrawOpts)
        : await stakingContract.farmWithdraw(amountTokenDesired, withdrawOpts);

    setTxStatus(TX_STATUS.Pending);
    setTransactionHash(transaction.hash);

    await transaction.wait(1);
    setTxStatus(TX_STATUS.Success);
    setView('success');
  } catch (e) {
    console.log(e);
    newrelicError(e, {
      method: 'withdrawFarm',
      input,
      account,
      farmContract,
    });

    const userRejected = USER_REJECTED_TEXTS.some(text =>
      (e as IError).message.includes(text),
    );
    console.log('Error withdrawing from farm: ', e);

    setError(true);

    if (!userRejected) {
      setErrorMessage(DEFAULT_ERROR_MESSAGE);
    }
    setView('error');
  }
};

export const claimFarmRewards = async (
  farmContract: string,
  setTxStatus: (status: TxType) => void,
  setTransactionHash: (hash: string) => void,
  setError: (error: boolean) => void,
  setErrorMessage: (errorMessage: string) => void,
  setView: (view: FarmsView) => void,
  account: string,
  signerInstance?: JsonRpcSigner,
  dynamicRewards?: boolean,
): Promise<void> => {
  try {
    setTxStatus(TX_STATUS.Confirm);

    const stakingContract = getStakingContract(
      farmContract,
      dynamicRewards,
      signerInstance,
    );

    const transaction = dynamicRewards
      ? await stakingContract.claimRewards()
      : await stakingContract.claimReward();

    setTxStatus(TX_STATUS.Pending);
    setTransactionHash(transaction.hash);
    await transaction.wait(1);

    setTxStatus(TX_STATUS.Success);
    setView('success');
  } catch (e) {
    newrelicError(e, {
      method: 'claimRewards',
      account,
      farmContract,
    });

    const userRejected = USER_REJECTED_TEXTS.some(text =>
      (e as IError).message.includes(text),
    );
    console.log('Error claiming rewards: ', e);

    setError(true);

    if (!userRejected) {
      setErrorMessage(DEFAULT_ERROR_MESSAGE);
    }
    setView('error');
  }
};
